import {LocalStorage} from "./LocalStorage";
import * as Sentry from "@sentry/react";
import {API_URL} from "../settings/local_settings";

// Header que identifica al origen del request para el middleware de actividad del Backend
// (proyecto Reemplazo de Intercom, Fase 1). Solo se cuenta la actividad de requests con este
// header en la whitelist (web-app | connect-portal). El FE web manda siempre "web-app".
// ⚠️ Requiere que el Backend tenga 'x-dashbo-client' en CORS_ALLOW_HEADERS (ya deployado):
// sin eso el preflight rechaza el header y el browser aborta los requests. Deploy Backend ANTES que FE.
export const DASHBO_CLIENT_HEADER = "X-Dashbo-Client";
export const DASHBO_CLIENT_HEADER_VALUE = "web-app";

const DEFAULT_CACHE_TTL = 30000; // 30 segundos
const _cache = new Map();    // cacheKey -> { data, timestamp }
const _inflight = new Map(); // cacheKey -> Promise

// Sólo se cachea lo exitoso. Un error transitorio (backend caído, microcorte de red) quedaría pegado
// 30s y se lo comerían todos los callers que compartan la URL — incluido el "volver a cargar" que
// ofrecen las pantallas cuando el fetch falló, que es justo el que necesita saltear el cache. Y los
// `ERROR_*` de flujo son peores: `ERROR_NO_AGENCY` gobierna el redirect a registro/invitación, así
// que cachearlo rebota al usuario a la pantalla que acaba de completar. `status` ausente = endpoint
// que devuelve data cruda; ahí no hay nada que descartar.
function isCacheableResponse(responseData) {
    if (!responseData) return false;
    return responseData.status === undefined || responseData.status === 'OK';
}

// 5xx ya reportados a Sentry en esta pestaña, por `método ruta status`. Ver `_reportServerError`.
const _reported5xx = new Set();

/** `https://api.../business-unit/19452/dashboard/metrics/` → `/business-unit/<id>/dashboard/metrics/` */
function _pathWithoutIds(url) {
    let path = url;
    try {
        path = new URL(url, window.location.origin).pathname;
    } catch (e) {
        // URL relativa o rara: se agrupa por lo que haya, que es mejor que no agrupar.
    }
    return path.replace(/\/\d+/g, '/<id>');
}

/** El cuerpo del error, acotado. El caso grande es el JSON de DRF, no el string. */
function _truncatedBody(errorBody) {
    if (typeof errorBody === 'string') return errorBody.slice(0, 1000);
    try {
        return JSON.stringify(errorBody).slice(0, 1000);
    } catch (e) {
        return '<cuerpo no serializable>';
    }
}

export class APIClient {
    constructor(url) {
        this.url = url;
    }

    static clearCache(urlPattern) {
        if (!urlPattern) {
            _cache.clear();
            return;
        }
        for (const key of _cache.keys()) {
            if (key.includes(urlPattern)) _cache.delete(key);
        }
    }

    /**
     * La key lleva el token adelante, no sólo la URL: sin eso, un logout seguido de un login con
     * OTRA cuenta dentro de la ventana del TTL le sirve al usuario nuevo la respuesta del anterior
     * (nombre, foto, flag de superadmin, módulos) — una fuga cross-agency de manual. Keyearlo acá
     * es infalible; depender de que cada camino de logout llame a `clearCache()` no lo es.
     */
    _cacheKey() {
        return (LocalStorage.fetchUserToken() || 'anon') + '|' + this.url;
    }

    /**
     * `options`, además del caché:
     * - `omitBodyFromReports: true` — un fallo de red NO manda el cuerpo del pedido a Sentry. Para lo
     *   que escribe el usuario en texto libre (la frase de «Crear una alerta › En tus palabras»: un
     *   cliente, un umbral, a quién avisar), que no tiene por qué salir a un proveedor externo.
     * - `expectedServerStatuses: [503]` — esos 5xx son respuestas del contrato del endpoint (la IA no
     *   contestó y la pantalla ofrece el modo guiado), no bugs: no se reportan.
     */
    call(method = 'GET', data = {}, options = {}) {
        const useCache = method === 'GET' && options.cache === true;
        const cacheTTL = options.cacheTTL || DEFAULT_CACHE_TTL;
        const cacheKey = useCache ? this._cacheKey() : null;

        if (useCache) {
            const cached = _cache.get(cacheKey);
            if (cached && (Date.now() - cached.timestamp) < cacheTTL) {
                return Promise.resolve(structuredClone(cached.data));
            }

            const inflight = _inflight.get(cacheKey);
            if (inflight) {
                return inflight.then(data => data ? structuredClone(data) : data);
            }
        }

        let headers = new Headers();
        const token = LocalStorage.fetchUserToken();
        if (token) {
            headers.append("Authorization", "Token " + token);
        }

        headers.append("Accept", "application/json");
        headers.append("Content-Type", "application/json");
        // Se setea SIEMPRE (no depende de extraHeaders). Va antes del loop de extraHeaders para
        // que un caller pueda sobreescribirlo si lo necesita, pero por defecto siempre viaja.
        headers.set(DASHBO_CLIENT_HEADER, DASHBO_CLIENT_HEADER_VALUE);

        if (options.extraHeaders) {
            Object.keys(options.extraHeaders).forEach((key) => {
                const value = options.extraHeaders[key];
                if (value !== null && value !== undefined) {
                    headers.set(key, value);
                }
            });
        }

        let requestConf = {
            method: method,
            headers: headers,
        };

        const dataIsNotEmpty = data !== null && data !== undefined && Object.keys(data).length > 0;
        if (dataIsNotEmpty) {
            requestConf['body'] = JSON.stringify(data)
        }
        const request = new Request(this.url, requestConf);

        const promise = fetch(request)
            .then((response) => this.handleResponse(response, method, options))
            .then((responseData) => {
                if (useCache && isCacheableResponse(responseData)) {
                    _cache.set(cacheKey, { data: responseData, timestamp: Date.now() });
                }
                return responseData;
            })
            .catch(error => {
                Sentry.captureException(error, {
                    extra: options.omitBodyFromReports
                        ? {url: this.url, method: method}
                        : {url: this.url, method: method, requestBody: data},
                });
                // Ante un fallo de red / microcorte del backend (ej. el del 2026-06-05, api.dashbo.io
                // saturado) devolvemos SIEMPRE un objeto en vez de undefined: asi los callers que hacen
                // `data.status` no crashean con "Cannot read properties of undefined (reading 'status')".
                // Usamos un status DEDICADO (NETWORK_ERROR), NO 'ERROR', a proposito: distingue "no
                // pudimos preguntar" de "el backend dijo que no", y no dispara los alert() legacy en
                // cada blip transitorio. El error ya quedo en Sentry arriba.
                //
                // NO hay clave `data`, y es una DECISION, no un olvido (FRONTEND-47). El invariante
                // de toda la capa es: `status !== 'OK'` => NO hay `data` utilizable. Agregarla no
                // arregla ningun crash y empeora el diagnostico:
                //  - `data: null` no evita el TypeError (`null.x` tira igual que `undefined.x`) y no
                //    cambia el comportamiento de NINGUN patron defensivo (`response.data?.x` y
                //    `response.data && ...` ya funcionan con la clave ausente). Lo unico que agrega
                //    es sugerir que hay un `data` -- justo el habito que causo el bug.
                //  - `data: {}` es peor: `.data.x` pasa a devolver `undefined` en silencio, asi que
                //    la pantalla PINTA un dato falso en vez de fallar (`UserEmailAlerts` dibujaria
                //    las once alertas apagadas; `BusinessUnitAccounts` pasaria `canEditCurrency:
                //    undefined`, que es distinto de null y DEJA PASAR la guarda del spinner), y donde
                //    el payload es una lista (`response.data.map`) revienta igual.
                // La guarda del caller va con `isOk()` (`utils/apiResponse.js`), nunca con
                // `status === 'ERROR'` -- ese literal es un colador: no matchea ni este NETWORK_ERROR
                // ni los codigos tipados del backend. Guardian: `__tests__/APIClient.errorShape.test.js`.
                // Ver sentry-bugs/BUG-02-FRONTEND-V-undefined-status.md
                return {
                    "status": "NETWORK_ERROR",
                    "error_code": "NETWORK_ERROR",
                    "error_message": "Se ha producido un error de conexión. Por favor, reintentá en unos segundos."
                };
            })
            .finally(() => {
                if (cacheKey) _inflight.delete(cacheKey);
            });

        if (useCache) {
            _inflight.set(cacheKey, promise);
        }

        return promise;
    }

    /**
     * Un 5xx es un bug del servidor y hasta hoy no dejaba rastro en NINGÚN lado: el `.catch()` de
     * abajo sólo ve fallos de red, y una respuesta 500 resuelve el fetch igual que un 200. El
     * Backend reporta a Sentry por su cuenta, pero cuando ese camino se corta —como la cuota de
     * errores agotada desde el 2026-09-08, que se comió 7 días de eventos— el 500 desaparece y
     * el usuario queda con un cartel genérico que nadie puede correlacionar con nada.
     *
     * Se reportan SÓLO los 5xx a propósito. Los 4xx son contrato: 401 de sesión vencida, 403 de
     * permisos, 404 de aislamiento cross-agency y 422 de error tipado son respuestas esperadas y
     * frecuentes — mandarlas a Sentry quemaría la cuota que justamente hace falta para ver esto.
     */
    _reportServerError(response, method, errorBody, options = {}) {
        if (response.status < 500) return;
        if ((options.expectedServerStatuses || []).includes(response.status)) return;
        // Una sola vez por (método, ruta sin ids, status) y por pestaña. Sin esto el cambio
        // MULTIPLICA lo que vino a rescatar: el Tablero pide `dashboard/metrics` una vez por
        // Línea de Control, así que una agencia con 18 líneas manda 18 eventos por recarga, por
        // usuario y por reintento — quemando la misma cuota cuyo agotamiento nos dejó 7 días
        // ciegos. El evento 19 no dice nada que no diga el primero.
        const key = `${method} ${_pathWithoutIds(this.url)} ${response.status}`;
        if (_reported5xx.has(key)) return;
        _reported5xx.add(key);

        try {
            Sentry.captureException(new Error(`HTTP ${response.status} en ${key}`), {
                // `fingerprint` explícito: el `new Error` se construye ACÁ, así que todos los 5xx
                // comparten stack y Sentry los agruparía por este archivo (o los fragmentaría uno
                // por business_unit_id, que es igual de inútil). Agrupa por endpoint.
                fingerprint: ['http-5xx', method, _pathWithoutIds(this.url)],
                // `tags` y no `extra`: en Sentry `extra` NO es buscable ni filtrable, que es justo
                // lo que hacía falta para contestar "¿qué endpoint está tirando 500?".
                tags: {http_status: String(response.status), http_method: method,
                       endpoint: _pathWithoutIds(this.url)},
                extra: {
                    url: this.url,
                    responseBody: _truncatedBody(errorBody),
                }
            });
        } catch (e) {
            // El reporte no puede degradar el contrato de error. Esto corre DENTRO del `.then()`
            // que tiene el `.catch()` de abajo: si el SDK lanza (init fallido, un adblocker que
            // rompe el transporte), ese catch lo absorbe y el caller recibe el objeto genérico,
            // sin `error_code`, `reason_code`, `http_status` ni `raw_body` — que es lo que leen el
            // 409 del fork de prompts y la ingesta de URLs de la Memoria.
        }
    }

    handleResponse(response, method = 'GET', options = {}) {
        if (!response.ok) {
            return response.json().then(errorBody => {
                this._reportServerError(response, method, errorBody, options);
                // Parse and return API-specific error messages, if any
                return {
                    "status": errorBody.status || "ERROR",
                    // "error_message": errorBody.message || "Unknown error occurred.",
                    "error_message": errorBody.detail || errorBody.message || errorBody.error_message || "Se ha producido un error interno. Si el error persiste, por favor contacte al equipo de Dashbo.",
                    "errors": errorBody.errors || [],
                    // El backend nuevo manda un código TIPADO en `error_code` (FORBIDDEN,
                    // INVALID_PARAMS, MISSING_CLIENT, NO_AGENCY…). Antes se pisaba con `status`,
                    // así que el componente nunca podía distinguir un 403 de permisos de un 500.
                    // Aditivo: si el cuerpo no trae `error_code` se cae al comportamiento de
                    // siempre (`status`, y "ERROR" si tampoco vino).
                    "error_code": errorBody.error_code || errorBody.status || "ERROR",
                    // Código tipado de un rechazo accionable (ej. la ingesta de URLs de la
                    // Memoria): el componente lo mapea a su texto i18n. undefined si no vino.
                    "reason_code": errorBody.reason_code,
                    // El status HTTP y el cuerpo crudo del error: el mapeo de arriba se comía
                    // respuestas tipadas que traen datos accionables (ej. el 409 del fork de la
                    // biblioteca de prompts, que incluye `fork_id`, o distinguir un 403 de
                    // permisos de un 500). Aditivo: ningún caller existente los mira.
                    "http_status": response.status,
                    "raw_body": errorBody
                };
            }).catch(() => {
                // Si el backend devuelve HTML (ej: 500 sin JSON), retornar un objeto de error.
                // Este es JUSTO el camino del cartel genérico que ve el usuario, así que el reporte
                // va también acá: el `_reportServerError` de arriba no corrió porque el `.json()`
                // rompió antes.
                this._reportServerError(response, method, '<cuerpo no-JSON>', options);
                return {
                    "status": "ERROR",
                    "error_message": "Se ha producido un error interno. Si el error persiste, por favor contacte al equipo de Dashbo.",
                    "error_code": response.status,
                    "http_status": response.status
                };
            });
        }
        // Process and return successful response
        return response.json();
    }

    download(method = 'GET', defaultFileName, handlerPromise) {
        let headers = new Headers();
        const token = LocalStorage.fetchUserToken();
        if (token) {
            headers.append("Authorization", "Token " + token);
        }

        headers.append("Content-Type", "txt/csv");
        headers.set(DASHBO_CLIENT_HEADER, DASHBO_CLIENT_HEADER_VALUE);

        let requestConf = {
            method: method,
            headers: headers,
        };

        const request = new Request(this.url, requestConf);

        return fetch(request)
            .then((response) => {
                if (!response.ok) {
                    // El cuerpo de un error puede NO ser JSON: el 404 de Django de una ruta que
                    // todavía no se deployó, un 500 o el 502/504 de Cloud Run. Sin el `catch`, el
                    // `json()` rechazaba en una promesa huérfana, el handler no se llamaba nunca y
                    // el botón de exportar quedaba en spinner para siempre (review C-HISTORIAL-01).
                    return response.json().catch(() => ({})).then(errorBody => {
                        // Parse and return API-specific error messages, if any
                        handlerPromise({
                            "status": errorBody.status || "ERROR",
                            "error_message": errorBody.detail || errorBody.message || errorBody.error_message || "Se ha producido un error interno. Si el error persiste, por favor contacte al equipo de Dashbo.",
                            "error_code": errorBody.status || "ERROR",
                            "http_status": response.status
                        });
                    });

                } else {
                    const filename = this._getFilename (response, defaultFileName);
                    response.blob().then(blob => {
                        this._getFileFromTemporalLink(blob, filename);
                    });
                    handlerPromise({"status": "OK"});
                }
            })
    }

    _getFileFromTemporalLink(blob, filename) {
        let url = window.URL.createObjectURL(blob);
        let tempLink = document.createElement('a');
        tempLink.href = url;
        tempLink.download = filename;
        tempLink.click();
    }

    _getFilename(response, defaultFileName) {
        let contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition !== null){
            return contentDisposition.split('filename=')[1];
        }
        else {
            return defaultFileName
        }
    }

    static getAgentSessionHistory(sessionId) {
        const url = `${API_URL}agent/sessions/${sessionId}/`;
        return new APIClient(url).call('GET');
    }

    static getAgentSessions(params = {}) {
        const query = new URLSearchParams();
        if (params.agency_client_id) query.set('agency_client_id', params.agency_client_id);
        if (params.status) query.set('status', params.status);
        if (params.visibility) query.set('visibility', params.visibility);
        const qs = query.toString();
        const url = `${API_URL}agent/sessions/${qs ? '?' + qs : ''}`;
        return new APIClient(url).call('GET');
    }

    static getAgentSessionsForClient(clientId) {
        const url = `${API_URL}agent/sessions/?agency_client_id=${clientId}`;
        return new APIClient(url).call('GET');
    }

    static renameAgentSession(sessionId, name) {
        const url = `${API_URL}agent/sessions/${sessionId}/rename/`;
        return new APIClient(url).call('PATCH', { name });
    }

    static archiveAgentSession(sessionId, archive = true) {
        return new APIClient(`${API_URL}agent/sessions/${sessionId}/archive/`).call('PATCH', { archive });
    }

    static deleteAgentSession(sessionId) {
        return new APIClient(`${API_URL}agent/sessions/${sessionId}/`).call('DELETE');
    }

    static updateAgentSessionVisibility(sessionId, visibility) {
        return new APIClient(`${API_URL}agent/sessions/${sessionId}/visibility/`).call('PATCH', { visibility });
    }

    login(credentials = {}) {
        let headers = new Headers();
        headers.append("Accept", "application/json");
        headers.append("Content-Type", "application/json");

        let requestConf = {
            method: 'POST',
            headers: headers,
        };

        requestConf['body'] = JSON.stringify(credentials);

        const request = new Request(this.url, requestConf);

        return fetch(request)
            .then((response) => {
                if (response.status === 401) {
                    return Promise.resolve('UNAUTHORIZED');
                }
                if (response.status === 201) {
                    return response.json();
                }
            });
    }

}
